import React, { useState, useRef } from 'react';
import ImageInfoOverlay from '../../components/ImageInfoOverlay/ImageInfoOverlay';
import ExperienceCard from '../../components/ExperienceCard/ExperienceCard';
import TelescopeIcon from '../../components/icons/TelescopeIcon';
import { getSectionData } from '../../data/usePortfolioData';
import useLazyBackground from '../../hooks/useLazyBackground';
import useScrollProgress from '../../hooks/useScrollProgress';

const Experience = () => {
  const [showOverlay, setShowOverlay] = useState(false);
  const sectionRef = useRef(null);
  const handleInfoClick = () => setShowOverlay(true);
  const handleClose = () => setShowOverlay(false);

  // Get experience data from centralized JSON
  const experienceData = getSectionData('experience');

  const scrollY = useScrollProgress(sectionRef);

  const bgLoaded = useLazyBackground(sectionRef, experienceData.backgroundImage);

  const sectionStyle = {
    ...(bgLoaded ? { backgroundImage: `url('${experienceData.backgroundImage}')` } : {}),
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };

  // Get experiences from centralized data
  const experiences = experienceData.experiences || [];

  return (
    <section 
      ref={sectionRef}
      id="experience" 
      className="experience-section relative min-h-screen flex flex-col justify-center items-center text-white py-16 sm:py-20 px-4 sm:px-6 lg:px-12 w-full" 
      style={sectionStyle}
    >
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-black/45 z-0"></div>
      
      {/* Info Button */}
      <button 
        className="absolute top-4 right-4 sm:top-6 sm:right-6 z-20 p-2 sm:p-3 bg-gradient-to-r from-space-blue-500/20 to-cosmic-purple-500/20 backdrop-blur-sm rounded-full hover:from-space-blue-500/40 hover:to-cosmic-purple-500/40 transition-all duration-300 border border-white/20 hover:border-white/40 group" 
        onClick={handleInfoClick}
        aria-label="Learn more about this background image"
        style={{
          transform: `translateY(${scrollY * 0.3}px)` // Subtle movement for button
        }}
      >
        <TelescopeIcon className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-110 transition-transform duration-300" />
      </button>
      
      {/* Content Container */}
      <div 
        className="relative z-10 max-w-7xl mx-auto w-full"
        style={{
          transform: `translateY(${scrollY * 0.5}px)` // Content moves faster than background
        }}
      >
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="font-display text-display-xl mx-auto mb-4 sm:mb-6 text-space-blue-100">
            {experienceData.title}
          </h2>
          <div className="w-20 sm:w-24 h-1 bg-gradient-to-r from-space-blue-400 to-cosmic-purple-400 mx-auto mb-6 sm:mb-8 rounded-full"></div>
        </div>
        
        {/* Experience Timeline - Single Column */}
        <div className="relative max-w-3xl mx-auto">
          {/* Vertical accent line */}
          <div className="absolute left-4 sm:left-5 top-2 bottom-2 w-px bg-gradient-to-b from-space-blue-400/50 via-space-blue-400/50 to-transparent" aria-hidden="true"></div>

          <div className="flex flex-col gap-8 sm:gap-10">
            {experiences.map((exp, index) => (
              <div
                key={index}
                className="relative animate-fade-in-up pl-12 sm:pl-14"
                style={{
                  animationDelay: `${index * 0.2}s`,
                  animationFillMode: 'both'
                }}
              >
                {/* Timeline marker */}
                <div className="absolute left-0 top-4 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-r from-space-blue-400 to-cosmic-purple-400 flex items-center justify-center shadow-lg shadow-space-blue-500/30">
                  <span className="text-white text-xs sm:text-sm font-bold">{index + 1}</span>
                </div>

                <ExperienceCard
                  company={exp.company}
                  position={exp.position}
                  location={exp.location}
                  duration={exp.duration}
                  achievements={exp.achievements}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Overlay Component */}
      {showOverlay && (
        <ImageInfoOverlay
          title={experienceData.overlayTitle}
          description={experienceData.overlayDescription}
          onClose={handleClose}
        />
      )}
    </section>
  );
};

export default Experience;